/**
 * useHoverThumbs - Hook for managing hover thumbnail cache
 *
 * Features:
 * - Progressive loading: starts coarse (1/100), then 1/50, 1/25, finally 1/10
 * - Uses IndexedDB for persistent storage
 * - Returns Map of index -> blob URL for tooltip display
 * - Background loading with progress tracking
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Photo } from '@/types';
import { hoverThumbCache } from '../services/HoverThumbCache';

const DEFAULT_INTERVAL = 10; // Final target: 1 thumb every 10 frames
const BATCH_SIZE = 10; // Load 10 thumbs at a time
const BATCH_DELAY = 50; // ms between batches (reduced for faster loading)

// Progressive granularity levels (coarse to fine)
const GRANULARITY_LEVELS = [100, 50, 25, 10];

export interface UseHoverThumbsReturn {
  thumbCache: Map<number, string>; // frameIndex -> blob URL
  isLoading: boolean;
  progress: { loaded: number; total: number };
  loadThumbs: () => void;
  clearCache: () => void;
}

/**
 * Hook for hover thumbnail preloading
 *
 * @param photos - Array of photos
 * @param viewId - View ID for cache isolation
 * @param interval - Load 1 thumb every N frames (default: 10)
 */
export function useHoverThumbs(
  photos: Photo[],
  viewId: string | undefined,
  interval: number = DEFAULT_INTERVAL
): UseHoverThumbsReturn {
  const [thumbCache, setThumbCache] = useState<Map<number, string>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const abortRef = useRef(false);
  const loadingRef = useRef(false);

  /**
   * Calculate which frame indices should have thumbs
   */
  const getTargetIndices = useCallback((): number[] => {
    if (photos.length === 0) return [];

    const indices: number[] = [];
    for (let i = 0; i < photos.length; i += interval) {
      indices.push(i);
    }

    // Always include last frame
    const lastIndex = photos.length - 1;
    if (!indices.includes(lastIndex)) {
      indices.push(lastIndex);
    }

    return indices;
  }, [photos.length, interval]);

  /**
   * Load cached thumbs into state (from IndexedDB)
   */
  const loadCachedThumbs = useCallback(async () => {
    if (!viewId || photos.length === 0) return;

    const targetIndices = getTargetIndices();
    const newCache = new Map<number, string>();

    for (const index of targetIndices) {
      const url = await hoverThumbCache.get(viewId, index);
      if (url) {
        newCache.set(index, url);
      }
    }

    setThumbCache(newCache);
    setProgress({ loaded: newCache.size, total: targetIndices.length });
  }, [viewId, photos.length, getTargetIndices]);

  /**
   * Get indices for a specific granularity level
   */
  const getIndicesForGranularity = useCallback((granularity: number): number[] => {
    if (photos.length === 0) return [];

    const indices: number[] = [];
    for (let i = 0; i < photos.length; i += granularity) {
      indices.push(i);
    }

    // Always include last frame
    const lastIndex = photos.length - 1;
    if (!indices.includes(lastIndex)) {
      indices.push(lastIndex);
    }

    return indices;
  }, [photos.length]);

  /**
   * Load thumbs with progressive granularity
   * Starts with coarse (1/100), then progressively finer (1/50, 1/25, 1/10)
   */
  const loadThumbs = useCallback(async () => {
    if (!viewId || photos.length === 0) return;

    // Prevent concurrent loads
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);

    try {
      const finalTargetIndices = getTargetIndices();
      const totalTarget = finalTargetIndices.length;

      // Progressive loading through granularity levels
      for (const granularity of GRANULARITY_LEVELS) {
        if (abortRef.current) break;

        const levelIndices = getIndicesForGranularity(granularity);

        // Find indices that need loading at this level
        const toLoad: number[] = [];
        for (const index of levelIndices) {
          if (abortRef.current) break;
          const cached = await hoverThumbCache.has(viewId, index);
          if (!cached) {
            toLoad.push(index);
          }
        }

        if (abortRef.current) break;
        if (toLoad.length === 0) continue; // All cached at this level

        // Load in batches
        for (let i = 0; i < toLoad.length; i += BATCH_SIZE) {
          if (abortRef.current) break;

          const batch = toLoad.slice(i, i + BATCH_SIZE);
          const batchPromises = batch.map(async (index) => {
            if (abortRef.current) return false;
            const photo = photos[index];
            if (!photo?.urls?.thumb200) return false;

            try {
              const success = await hoverThumbCache.store(viewId, index, photo.urls.thumb200);
              return success;
            } catch {
              return false;
            }
          });

          await Promise.all(batchPromises);

          if (abortRef.current) break;

          // Update progress based on total target
          const currentCount = await hoverThumbCache.getCount(viewId);
          setProgress({ loaded: Math.min(currentCount, totalTarget), total: totalTarget });

          // Reload cache into state after each batch for immediate feedback
          await loadCachedThumbs();

          // Small delay between batches to avoid blocking UI
          if (i + BATCH_SIZE < toLoad.length && !abortRef.current) {
            await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY));
          }
        }

        // After each granularity level, ensure cache is updated
        if (!abortRef.current) {
          await loadCachedThumbs();
        }
      }

      // Final reload of cache into state
      if (!abortRef.current) {
        await loadCachedThumbs();
        const finalCount = await hoverThumbCache.getCount(viewId);
        setProgress({ loaded: finalCount, total: totalTarget });
      }
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [viewId, photos, getTargetIndices, getIndicesForGranularity, loadCachedThumbs]);

  /**
   * Clear cache for current view
   */
  const clearCache = useCallback(async () => {
    if (!viewId) return;

    abortRef.current = true;
    await hoverThumbCache.clearView(viewId);
    setThumbCache(new Map());
    setProgress({ loaded: 0, total: 0 });
  }, [viewId]);

  /**
   * Load cached thumbs on mount and when photos change
   */
  useEffect(() => {
    loadCachedThumbs();
  }, [loadCachedThumbs]);

  /**
   * Cleanup on unmount only
   */
  useEffect(() => {
    return () => {
      abortRef.current = true;
    };
  }, []);

  /**
   * Auto-start loading and continue if incomplete
   * Also handles cache invalidation when photos.length changes
   */
  useEffect(() => {
    if (!viewId || photos.length === 0) return;

    // Reset abort flag when starting fresh
    abortRef.current = false;

    // Start loading after a short delay (let UI settle first)
    const timer = setTimeout(async () => {
      // Check if cache is stale (photos count changed since last cache)
      if (hoverThumbCache.isCacheStale(viewId, photos.length)) {
        console.log('[useHoverThumbs] Cache stale, clearing (photos count changed)');
        await hoverThumbCache.clearView(viewId);
        setThumbCache(new Map());
        setProgress({ loaded: 0, total: 0 });
      }

      const count = await hoverThumbCache.getCount(viewId);
      const targetCount = getTargetIndices().length;

      // Load if not complete (continue from where we left off)
      if (count < targetCount) {
        loadThumbs();
      }

      // Update metadata with current photos count
      hoverThumbCache.setViewMetadata(viewId, photos.length);
    }, 500);

    return () => clearTimeout(timer);
  }, [viewId, photos.length, getTargetIndices, loadThumbs]);

  return {
    thumbCache,
    isLoading,
    progress,
    loadThumbs,
    clearCache,
  };
}
